/**
 * When to tell a river, a national forest or a wilderness. A point feature triggers on approach;
 * these have no point. A river becomes worth telling once the road has run beside it for a
 * while, a forest once the road has been inside it, a wilderness (no road enters one) once the
 * road has run along its edge. Then it plays in the next quiet gap: after any due landmark,
 * before area filler, and without filler's ten-minute wait. On H27 the North Santiam ran beside
 * OR-22 for seventy minutes and two quiet stretches (13:05-13:35, 13:43-14:26) went unused.
 *
 * A region (an EPA Level III ecoregion, plan section 9) counts like a forest, once the car has
 * arrived: the one the trip started in plays only after the car has left it and come back.
 *
 * Like the speech queue and the filler, this runs off the location fixes, never a timer.
 */
import { bearingDiff, haversineM } from './geo';
import { distanceToFeature, insideRings, nearestOnLines } from './featureGeo';
import type { QueueView } from './filler';
import type { SpeechItem } from './speechQueue';
import { spokenTitle } from './intro';
import type { CleanFix, Feature } from './types';

export interface AlongOptions {
  /** A river counts as beside the road within this distance of its line. */
  besideM: number;
  /** A wilderness counts within this distance of its outline (inside it too, though no road is). */
  wildernessM: number;
  /** ...and when the road runs within this many degrees of the line's direction, either way. */
  headingDeg: number;
  /** Distance run beside or inside before a feature is eligible. */
  runM: number;
  /** Bends away shorter than this do not break a run; a crossing at a bridge never adds to one. */
  gapM: number;
  /** A river further than this from the car is no longer eligible. */
  leaveM: number;
  /** A forest or wilderness is no longer eligible after this far outside or away from it. */
  leaveOutsideM: number;
  /** A single step longer than this (a jump after a tunnel) counts only this much. */
  maxStepM: number;
}

export const ALONG_DEFAULTS: AlongOptions = {
  besideM: 1500,
  wildernessM: 2000,
  headingDeg: 35,
  runM: 3000,
  gapM: 1000,
  leaveM: 5000,
  leaveOutsideM: 3000,
  maxStepM: 500,
};

interface Run {
  runM: number;
  gapM: number;
  awayM: number;
  eligible: boolean;
  /** Where eligibility began, for the log. */
  since: { lat: number; lon: number } | null;
}

/** Angle between the road and a line, ignoring which way the line was drawn: 0-90. */
export function offLine(heading: number, lineBearing: number): number {
  const d = Math.abs(bearingDiff(heading, lineBearing));
  return Math.min(d, 180 - d);
}

/**
 * Accumulates, per feature, how far the car has run beside (or inside) it. A feature's run only
 * changes on fixes where its shape is known; offline, the last state holds.
 */
export class AlongTracker {
  readonly opts: AlongOptions;
  private runs = new Map<number, Run>();
  private last: { lat: number; lon: number } | null = null;

  constructor(opts: Partial<AlongOptions> = {}) {
    this.opts = { ...ALONG_DEFAULTS, ...opts };
  }

  reset(): void {
    this.runs.clear();
    this.last = null;
  }

  /** Is the car beside or inside `f` at this fix? Null when it cannot tell (a river with no heading). */
  beside(f: Feature, lat: number, lon: number, heading: number | null): boolean | null {
    const o = this.opts;
    if (f.kind === 'forest' || f.kind === 'region') return insideRings(lat, lon, f.geom);
    if (f.kind === 'wilderness') return distanceToFeature(lat, lon, 'wilderness', f.geom) <= o.wildernessM;
    const n = nearestOnLines(lat, lon, f.geom);
    if (!n || n.distM > o.besideM) return false;
    if (heading == null) return null;
    return offLine(heading, n.bearing) <= o.headingDeg;
  }

  /** Feed one clean fix and the features whose shape is known here. Returns ids that stopped being eligible. */
  update(fix: CleanFix, features: readonly Feature[]): number[] {
    const o = this.opts;
    const lat = fix.coords.latitude;
    const lon = fix.coords.longitude;
    const step = this.last ? Math.min(o.maxStepM, haversineM(this.last.lat, this.last.lon, lat, lon)) : 0;
    this.last = { lat, lon };
    const lost: number[] = [];
    for (const f of features) {
      let r = this.runs.get(f.page_id);
      if (!r) this.runs.set(f.page_id, (r = { runM: 0, gapM: 0, awayM: 0, eligible: false, since: null }));
      const b = this.beside(f, lat, lon, fix.heading);
      if (b === null) continue;
      if (b) {
        r.runM += step;
        r.gapM = 0;
        r.awayM = 0;
        if (!r.eligible && r.runM >= o.runM) {
          r.eligible = true;
          r.since = { lat, lon };
        }
        continue;
      }
      r.gapM += step;
      if (r.gapM > o.gapM) r.runM = 0;
      if (!r.eligible) continue;
      // Beside a river means close and parallel; a bend or a crossing is not leaving it.
      const gone = f.kind === 'river' ? distanceToFeature(lat, lon, 'river', f.geom) > o.leaveM : (r.awayM += step) > o.leaveOutsideM;
      if (gone) {
        r.eligible = false;
        r.since = null;
        lost.push(f.page_id);
      }
    }
    return lost;
  }

  isEligible(pageId: number): boolean {
    return this.runs.get(pageId)?.eligible ?? false;
  }

  /** Metres run beside the feature so far (for choosing between two and for the log). */
  runM(pageId: number): number {
    return this.runs.get(pageId)?.runM ?? 0;
  }
}

export interface FeatureDeps {
  /** Features with a script whose shape is known round the car (the latest corridor call's). */
  featuresHere(fix: CleanFix): readonly Feature[] | Promise<readonly Feature[]>;
  /** Played this trip, resting (90 days; a region, ever), or covered by a branch told as one. */
  isPlayed(pageId: number, kind: Feature['kind']): boolean | Promise<boolean>;
  /** Would an unplayed landmark trigger within this many seconds? (ProximityEngine.upcoming) */
  landmarkSoon(fix: CleanFix, withinSec: number): boolean | Promise<boolean>;
}

export interface FeatureSchedulerOptions extends AlongOptions {
  /** Clear road wanted beyond the story's own length before starting it. */
  marginSec: number;
}

/**
 * The river the road follows is the most specific story, then the wilderness beside it, then the
 * forest, then the region around them all.
 */
const KIND_ORDER: Record<Feature['kind'], number> = { river: 0, wilderness: 1, forest: 2, region: 3 };

/**
 * A region the car is inside when its outline first arrives, this early in the trip, is where the
 * trip started: starting a trip is not arriving (towns follow the same rule, ProximityEngine).
 */
export const START_REGION_M = 5000;

export interface FeaturePick {
  /** A feature to enqueue now, if any. */
  play: Feature | null;
  /** Features that stopped being eligible this fix: drop them if still queued. */
  lost: number[];
}

/**
 * Picks a feature for the next quiet gap. The host asks on every fix and enqueues what comes back
 * with featureItem(); a landmark queued or speaking always goes first.
 */
export class FeatureScheduler {
  readonly tracker: AlongTracker;
  readonly marginSec: number;
  /** Distance driven this trip, for telling the start region from one arrived in. */
  private tripM = 0;
  private last: { lat: number; lon: number } | null = null;
  private seenRegions = new Set<number>();
  /** Regions the trip started in and has not yet left, with how far the car has been outside each. */
  private startRegions = new Map<number, number>();

  constructor(
    private deps: FeatureDeps,
    opts: Partial<FeatureSchedulerOptions> = {},
  ) {
    this.tracker = new AlongTracker(opts);
    this.marginSec = opts.marginSec ?? 20;
  }

  reset(): void {
    this.tracker.reset();
    this.tripM = 0;
    this.last = null;
    this.seenRegions.clear();
    this.startRegions.clear();
  }

  /** Is this the region the trip started in, not yet left? */
  isStartRegion(pageId: number): boolean {
    return this.startRegions.has(pageId);
  }

  private observeRegions(fix: CleanFix, here: readonly Feature[]): void {
    const lat = fix.coords.latitude;
    const lon = fix.coords.longitude;
    const step = this.last ? Math.min(this.tracker.opts.maxStepM, haversineM(this.last.lat, this.last.lon, lat, lon)) : 0;
    this.last = { lat, lon };
    this.tripM += step;
    for (const f of here) {
      if (f.kind !== 'region') continue;
      const inside = insideRings(lat, lon, f.geom);
      if (!this.seenRegions.has(f.page_id)) {
        this.seenRegions.add(f.page_id);
        if (inside && this.tripM <= START_REGION_M) this.startRegions.set(f.page_id, 0);
        continue;
      }
      const out = this.startRegions.get(f.page_id);
      if (out == null) continue;
      // Leaving counts once the car has been well outside, so a border wobble is not an arrival.
      const next = inside ? 0 : out + step;
      if (next > this.tracker.opts.leaveOutsideM) this.startRegions.delete(f.page_id);
      else this.startRegions.set(f.page_id, next);
    }
  }

  async pick(fix: CleanFix, queue: QueueView): Promise<FeaturePick> {
    const here = await this.deps.featuresHere(fix);
    this.observeRegions(fix, here);
    const lost = this.tracker.update(fix, here);
    if (queue.speaking || queue.queue.length) return { play: null, lost };
    const ready = here
      .filter((f) => this.tracker.isEligible(f.page_id) && !this.startRegions.has(f.page_id))
      .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || this.tracker.runM(b.page_id) - this.tracker.runM(a.page_id));
    for (const f of ready) {
      if (await this.deps.isPlayed(f.page_id, f.kind)) continue;
      // Wait rather than fall through: a landmark is about to speak, and this one will keep.
      if (await this.deps.landmarkSoon(fix, f.est_seconds + this.marginSec)) return { play: null, lost };
      return { play: f, lost };
    }
    return { play: null, lost };
  }
}

/**
 * The speech item for a feature: its name as the opener, then the script, behind any landmark. A
 * region may carry a live preface ("The road here is at about twenty-four hundred feet."), which
 * the phone works out from its own terrain tiles; the cached script never states it.
 */
export function featureItem(f: Feature, voice?: string, preface?: string | null): SpeechItem {
  const opener = `${spokenTitle(f.title)}.${preface ? ` ${preface}` : ''}`;
  return { pageId: f.page_id, kind: 'feature', text: f.script, prelude: () => opener, distance_m: 0, estSeconds: f.est_seconds + (preface ? 5 : 2), voice };
}

const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const under100 = (n: number): string => (n < 20 ? ONES[n]! : TENS[Math.floor(n / 10)]! + (n % 10 ? `-${ONES[n % 10]}` : ''));

/** Feet the way the scripts say them: 2,400 is "twenty-four hundred", 3,000 "three thousand". */
export function spokenFeet(ft: number): string {
  if (ft % 1000 === 0) return `${under100(ft / 1000)} thousand`;
  if (ft < 10_000) return `${under100(ft / 100)} hundred`;
  return `${under100(Math.floor(ft / 1000))} thousand ${under100((ft % 1000) / 100)} hundred`;
}

/** Below this the preface says nothing worth hearing. */
export const PREFACE_MIN_FT = 300;

/**
 * "The road here is at about twenty-four hundred feet." from the car's elevation in metres,
 * rounded to the nearest hundred feet (plan section 4). Null when unknown or near sea level.
 */
export function elevationPreface(m: number | null | undefined): string | null {
  if (m == null || !Number.isFinite(m)) return null;
  const ft = Math.round((m * 3.28084) / 100) * 100;
  if (ft < PREFACE_MIN_FT) return null;
  return `The road here is at about ${spokenFeet(ft)} feet.`;
}
